import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Meta, Display, Card, PageTitle, Icons } from '../components/Atoms';
import Layout, { TopBar } from '../components/Layout';
import { LoadingState, EmptyState } from '../components/states';
import { useApi } from '../lib/api';
import axios from '../../config/axios';

/**
 * FriendGarden — read-only widok ogrodu znajomego (S6/T17).
 * Endpoint: GET /api/social/friends/:userId/garden
 * Wymagane: areFriends accepted + share_garden_with_friends=1
 */
export default function FriendGarden() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useApi(`/api/social/friends/${userId}/garden`);

  if (loading) {
    return (
      <Layout topBar={<TopBar left={<Meta>Ogród znajomego</Meta>} />}>
        <div style={{ padding: 32 }}><LoadingState /></div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout topBar={<TopBar left={<Meta>Ogród znajomego</Meta>} />}>
        <div style={{ display: 'grid', placeItems: 'center', padding: 32, minHeight: 'calc(var(--t-vh) - 80px)' }}>
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <Meta color="amber" style={{ marginBottom: 14 }}>403</Meta>
            <Display size={48} style={{ display: 'block', marginBottom: 14 }}>Brak dostępu</Display>
            <p style={{ fontSize: 14, color: 'var(--t-text-dim)', marginBottom: 24 }}>
              Ten ogród jest prywatny albo nie jesteś znajomym właściciela.
            </p>
            <button onClick={() => navigate(-1)} style={{
              padding: '12px 24px', borderRadius: 12, background: 'var(--t-sage)',
              color: 'var(--t-bg)', border: 'none', cursor: 'pointer', fontFamily: 'var(--t-sans)', fontSize: 14
            }}>← Wróć</button>
          </div>
        </div>
      </Layout>
    );
  }

  const { plots, beds, gallery, own } = data;

  return (
    <Layout topBar={<TopBar left={<Meta>{own ? 'Twój ogród' : 'Ogród znajomego'}</Meta>} />}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 32px 60px' }}>
        <PageTitle
          eyebrow={own ? 'Twój ogród' : 'Ogród znajomego'}
          title="Podgląd ogrodu"
          sub="Widok read-only — zmiany możesz wprowadzać tylko na własnych poletkach."
        />

        {/* Poletka */}
        <section style={{ marginBottom: 40 }}>
          <Meta>Grunta</Meta>
          {plots.length === 0 ? (
            <EmptyState title="Brak poletek" description="Właściciel jeszcze nie dodał poletek." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
              {plots.map(p => (
                <Card key={p.id}>
                  <div style={{ marginBottom: 6 }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginBottom: 8 }}>{p.description}</div>}
                  {p.size && <Meta><Icons.Plot size={10} style={{ marginRight: 4 }} />{p.size}</Meta>}
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Grządki */}
        <section style={{ marginBottom: 40 }}>
          <Meta>Grządki</Meta>
          {beds.length === 0 ? (
            <EmptyState title="Brak grządek" description="Właściciel jeszcze nie dodał grządek." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginTop: 12 }}>
              {beds.map(b => (
                <Card key={b.id} style={{ borderLeft: '3px solid var(--t-sage)' }}>
                  <div>{b.plant_name || b.name || 'Pusta'}</div>
                  {b.plot_name && <Meta style={{ marginTop: 4 }}><Icons.Plot size={10} style={{ marginRight: 4 }} />{b.plot_name}</Meta>}
                  {b.planted_at && <Meta style={{ marginTop: 2 }}><Icons.Calendar size={10} style={{ marginRight: 4 }} />{b.planted_at}</Meta>}
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Galeria */}
        <section style={{ marginBottom: 40 }}>
          <Meta>Galeria</Meta>
          {gallery.length === 0 ? (
            <EmptyState title="Brak zdjęć" description="Właściciel jeszcze nie dodał zdjęć." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginTop: 12 }}>
              {gallery.map(g => (
                <div key={g.id} style={{ aspectRatio: '1/1', borderRadius: 8, background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)', overflow: 'hidden' }}>
                  <img src={g.photo_path.startsWith('/') ? g.photo_path : `/${g.photo_path}`} alt={g.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          )}
        </section>

        <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--t-line)' }}>
          <Link to="/feed" style={{ fontSize: 13, color: 'var(--t-sage)', textDecoration: 'none' }}>← Wróć do feedu</Link>
        </div>
      </div>
    </Layout>
  );
}
